import type { Sprungziel } from './Meldungen'
import { satz } from './sprache'
import type { Textbaustein } from './typen'
import { Verweissatz } from './Verweissatz'

/**
 * **Ein Rechenabbruch sagt, was zu tun ist, und führt dorthin** (7.1
 * Abschnitt 13, Nachtrag 2026-10-11, R1581–R1583).
 *
 * Der Befund: Nach *Rechnen* unter Konzernabschlüsse stand *„Für Konto 2000
 * Gezeichnetes Kapital … fehlt die Zeile der historischen Kurse“* — und kein
 * Weg dorthin. *„Hier fehlt der Absprung zum Ort, um den Fehler zu beheben.“*
 * Jeder Rechenabbruch bringt seinen Weg als Satz mit (die Handreichung); hier
 * steht, wohin dieser Satz führt.
 *
 * **Am Weg, nicht an der Meldung:** Zwei Meldungen, die denselben Weg haben —
 * eine Zeile fehlt, eine Summe passt nicht —, führen an denselben Ort.
 */

/** Was der Weg über seine Lage weiß, ohne dass der Rechenkern es schickt: Kreis und Bewertungsebene. */
export type Abbruchkontext = { kreis?: string | null; ledger?: string | null }

function wert(baustein: Textbaustein, feld: string): string | undefined {
  return baustein.werte.find((w) => w.feld === feld)?.wert
}

/** Ohne leere Angaben — eine Auswahl, die nichts sagt, ist keine (R1528). */
function auswahl(paare: Record<string, string | null | undefined>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(paare).filter((p): p is [string, string] => typeof p[1] === 'string' && p[1] !== ''),
  )
}

/**
 * Wohin der Weg eines Rechenabbruchs führt (R1581). `null` heißt: noch kein
 * Verweis — dann steht der Satz ohne da, und der Weg steht in
 * {@link OHNE_VERWEIS}.
 */
export function verweisJeAbbruch(
  handreichung: Textbaustein | null | undefined,
  kontext: Abbruchkontext = {},
): Sprungziel | null {
  if (!handreichung) {
    return null
  }

  switch (handreichung.schluessel) {
    // R1583: mit Gesellschaft und Monat der Meldung vorgewählt.
    case 'abbruch.historischer-kurs.weg':
      return {
        bereich: 'stammdaten',
        unterpunkt: 'historische-kurse',
        auswahl: auswahl({
          einheit: wert(handreichung, 'einheit'),
          periode: wert(handreichung, 'periode'),
          kreis: kontext.kreis,
          ledger: kontext.ledger,
        }),
      }
    // R1691: Einen geschlossenen Monat öffnet man im Konzernabschluss-Monitor wieder.
    case 'abbruch.periode.geschlossen.weg':
    case 'abbruch.periode.geschlossen.weg.jahresende':
      return { bereich: 'abschluss', unterpunkt: 'monitor' }
    // R1592: Die fehlende Lieferung vor dem Eintritt wird im Datenimport eingelesen.
    case 'abbruch.kreiseintritt.ohne-stand.weg':
      return { bereich: 'datenimport', unterpunkt: 'lieferungen' }
    case 'abbruch.eintrittskurs.fehlt.weg':
      return { bereich: 'konzernstruktur', unterpunkt: 'kreise' }
    case 'abbruch.differenzkonto-fehlt.weg':
    case 'abbruch.differenzkonto-waehrung-fehlt.weg':
    // R1605: Die Positionen der Kapitalkonsolidierung wählt der Prozess.
    // R1610: Die Schritte eines Konzernabschlusses pflegt man an ihm.
    case 'abbruch.kapitalkonsolidierung.ohne-positionen.weg':
      return { bereich: 'abschluss', unterpunkt: 'konzernabschluesse' }
    default:
      return null
  }
}

/**
 * **Die Wege, die noch keinen Verweis tragen** (R1582).
 *
 * Gebaut wird zuerst, was in den Daten vorkommt; die Liste darf nur kürzer
 * werden, und ihr Testfall meldet jeden Weg, der neu dazukommt, ohne dass er
 * einen Verweis hat. Viele davon stehen nicht nach einem Lauf, sondern beim
 * Erfassen oder Pflegen — dort zeigt die Seite den Satz, an der der Anwender
 * gerade steht.
 */
export const OHNE_VERWEIS: readonly string[] = [
  'abbruch.aendern.belegart.weg',
  'abbruch.aendern.storniert.weg',
  'abbruch.aufsatz.angabe-fehlt.weg',
  'abbruch.beleg.unbekannt.weg',
  'abbruch.berichtsart.weg',
  'abbruch.bestand.vormonat-fehlt.weg',
  'abbruch.beteiligung.ohne-eintritt.weg',
  'abbruch.datenbank.beleg.gruppe.weg',
  'abbruch.datenbank.beleg.nichtauf.weg',
  'abbruch.datenbank.beleg.rohdaten.weg',
  'abbruch.datenbank.beleg.statistikgemischt.weg',
  'abbruch.datenbank.berichtigen.belegart.weg',
  'abbruch.datenbank.berichtigen.protokoll.weg',
  'abbruch.datenbank.berichtigen.storniert.weg',
  'abbruch.datenbank.berichtigen.unveraenderlich.weg',
  'abbruch.datenbank.konto.statistikwechsel.weg',
  'abbruch.datenbank.loeschen.belegart.weg',
  'abbruch.datenbank.loeschen.geschlossen.weg',
  'abbruch.datenbank.loeschen.protokoll.weg',
  'abbruch.datenbank.loeschen.storniert.weg',
  'abbruch.datenbank.saldowechsel.bericht.weg',
  'abbruch.datenbank.saldowechsel.kontobericht.weg',
  'abbruch.datenbank.saldowechsel.kontoposition.weg',
  'abbruch.datenbank.saldowechsel.kreis.weg',
  'abbruch.datenbank.saldowechsel.positionkonto.weg',
  'abbruch.datenbank.saldowechsel.summe.weg',
  'abbruch.datenbank.saldowechsel.zielfehlt.weg',
  'abbruch.datenbank.saldowechsel.zielsumme.weg',
  'abbruch.datenbank.sprache.unbekannt.weg',
  'abbruch.differenzkonto-unbekannt.weg',
  'abbruch.erfassung.begruendung.weg',
  'abbruch.erfassung.bewegungsart.guv.weg',
  'abbruch.erfassung.bewegungsart.unbekannt.weg',
  'abbruch.erfassung.bewegungsart.weg',
  'abbruch.erfassung.doppelt.weg',
  'abbruch.erfassung.geschlossen.weg',
  'abbruch.erfassung.konto.weg',
  'abbruch.erfassung.name.weg',
  'abbruch.erfassung.statistikgemischt.weg',
  'abbruch.erfassung.zeilen.weg',
  'abbruch.erstkonsolidierung-fehlt.weg',
  'abbruch.ezb.leer.weg',
  'abbruch.ezb.nichterreichbar.weg',
  'abbruch.ezb.paar.weg',
  'abbruch.handbuch.weg',
  'abbruch.jahresergebnis-fehlt.weg',
  'abbruch.jahreswechsel-ohne-dezember.weg',
  'abbruch.kapitalerhoehung.kurs.weg',
  'abbruch.kapitalkonsolidierung.buchwert-bewegt.weg',
  'abbruch.kapitalkonsolidierung.vormonat-fehlt.weg',
  'abbruch.ergebnisvortrag-ohne-vormonat.weg',
  'abbruch.kapitaltransaktion.abgang-ohne-eintrag.weg',
  'abbruch.kapitaltransaktion.eintrag-ohne-bewegung.weg',
  'abbruch.kapitaltransaktion.erhoehung-ohne-eintrag.weg',
  'abbruch.kapitaltransaktion.erhoehung-quote.weg',
  'abbruch.kapitaltransaktion.quote.weg',
  'abbruch.kapitaltransaktion.vor-dem-start.weg',
  'abbruch.kapitaltransaktion.zugang-ohne-eintrag.weg',
  'abbruch.kapitaltransaktion.zukauf-passiv.weg',
  'abbruch.kein-eigenkapital.weg',
  'abbruch.kein-regelwerkskapitel.weg',
  'abbruch.keine-regelwerksausgabe.weg',
  'abbruch.keine-salden.weg',
  'abbruch.kreis-ohne-einheit.weg',
  'abbruch.kreis-unbekannt.weg',
  'abbruch.kurs-fehlt.weg',
  'abbruch.lauf-unbekannt.weg',
  'abbruch.ledger-unbekannt.weg',
  'abbruch.mappe-leer.weg',
  'abbruch.mappe-zu-gross.weg',
  'abbruch.pruefregel-periode.weg',
  'abbruch.pruefregel-unbekannt.weg',
  'abbruch.pruefregel-untergrenze.weg',
  'abbruch.raster.weg',
  'abbruch.spiegelart.weg',
  'abbruch.umkehrung.belegart.weg',
  'abbruch.umkehrung.kette.weg',
  'abbruch.umkehrung.schon.weg',
  'abbruch.umkehrung.zeitpunkt.weg',
  'abbruch.unterschiedsbetrag-konto.weg',
  'abbruch.unterschiedsbetrag-kontoart.weg',
  'abbruch.zeitachse.anfang.weg',
  'abbruch.zeitachse.bestand.weg',
  'abbruch.zeitachse.ende.weg',
  'abbruch.zeitachse.unstimmig.weg',
  'abbruch.zwischengewinn.marge-fehlt.weg',
]

/** Die Adresse eines Ziels — dieselbe Form, die die Anwendung beim Sprung setzt. */
export function adresseVon(ziel: Sprungziel): string {
  const pfad = ziel.unterpunkt ? `${ziel.bereich}/${ziel.unterpunkt}` : ziel.bereich
  const frage = ziel.auswahl && Object.keys(ziel.auswahl).length > 0 ? `?${new URLSearchParams(ziel.auswahl)}` : ''
  return `#/${pfad}${frage}`
}

/**
 * Der Weg eines Rechenabbruchs als Satz — mit dem Ziel als Verweis, wo es eins
 * gibt (R1581). Der Sprung setzt die Adresse; die Anwendung folgt ihr wie
 * jedem Klick in der Leiste.
 */
export function Abbruchweg({
  handreichung,
  kontext,
}: {
  handreichung: Textbaustein | null | undefined
  kontext?: Abbruchkontext
}) {
  if (!handreichung) {
    return null
  }

  const ziel = verweisJeAbbruch(handreichung, kontext)
  return (
    <Verweissatz
      text={satz(handreichung, undefined, true)}
      aufVerweis={ziel ? () => (window.location.hash = adresseVon(ziel)) : null}
    />
  )
}
